// P2 — **THE MEMBER PHONE POPUP: ONE ASK, AND EITHER ANSWER ENDS IT FOREVER.**
//
// Her order: *"Existing members with no phone on file see ONE warm popup in the house language, the
// reason before the ask ... Phone field, the consent line, one gold save, a plain 'not now'. >>> THE
// ONCE LAW: saving OR declining records the answer TO THE DATABASE and the popup never shows again.
// No nagging ever. <<< Never during a generation or mid-conversation — only on a clean room
// landing."*
//
// **THE DECISION IS NOT IN HERE, AND THAT IS THE POINT.** `phonePromptState` in `src/memberPhone.js`
// imports nothing, so a `.mjs` gate LOADS it and drives every combination of her clauses with no
// browser — where a ternary written inside this component could only ever be read by a regex over a
// TSX file (R8/R95). This component fetches two facts, hands them to the law, and draws what it is
// told.
//
// **IT MOUNTS BESIDE `InstallPrompt` AND ASKS THE SAME QUESTIONS OF THE SAME PAGE**, because they
// are the same kind of surface — a once-per-account offer on a clean room landing — and two
// different notions of *may we interrupt her* on one screen is how two popups end up stacked.
import { useEffect, useRef, useState } from "react";
import { MessageSquare } from "lucide-react";
import { CLOSE_OVERLAYS_EVENT } from "./MobileNav";
import { useActiveGenerations } from "./GenerationStrips";
import {
  PHONE_PROMPT_COPY, SMS_CONSENT_TEXT, phonePromptState, phonePromptOffered, phonePromptPage,
} from "../memberPhone.js";
import { LANDING_OFFERS, mayOpen } from "../landingOffers.js";
import { PLATFORM, isStandalone, platformOf, shouldOfferInstall } from "../installPrompt.js";

type Facts = { known: boolean; hasPhone: boolean; answeredAt: string | null };

export function PhonePrompt({
  signedIn = false,
  hasRoom = false,
  page = "",
}: {
  signedIn?: boolean;
  hasRoom?: boolean;
  page?: string;
}) {
  // **`known: false` UNTIL HER ROW ANSWERS, WHICH THE LAW READS AS `hold`.** The default is the one
  // that draws nothing: a popup that flashes for a frame on every load is nagging nobody can even
  // report properly (L5.1's own reported defect one surface over).
  const [facts, setFacts] = useState<Facts>({ known: false, hasPhone: true, answeredAt: null });
  // INSTALL-1's sequencing ruling. `known: false` is the default that draws NOTHING, exactly as the
  // phone facts above: until the install state has answered, this landing belongs to nobody.
  const [installFacts, setInstallFacts] = useState<{
    known: boolean; answer: string | null; answeredAt: string | null; asks: number;
  }>({ known: false, answer: null, answeredAt: null, asks: 0 });
  const [phone, setPhone] = useState("");
  const [consent, setConsent] = useState(false);
  const [note, setNote] = useState("");
  const [closed, setClosed] = useState(false);
  // D-5's ruling, one control over: a `useState` guard is read from the render that BOUND the
  // handler, so two taps inside one frame both send. A ref is the only guard that holds.
  const sending = useRef(false);
  const asked = useRef(false);

  // HER CLAUSE 5, BOTH HALVES, HELD HERE RATHER THAN IN THE SHELL — nothing else reads either value,
  // and this is the surface they are about.
  //
  // **THE GENERATION HALF IS THE SERVER'S ANSWER AND NEVER A LOCAL BUSY FLAG.** A local flag dies on
  // navigation (R19's own finding one room over), so a member who started a reading and walked back
  // to her room would meet the popup over work she is waiting on. This is the same hook the dock
  // reads, so there is one answer to *is something being made for her*.
  // **AND IT IS ONLY ASKED WHERE THE POPUP COULD OPEN AT ALL.** This component mounts on every page
  // (it returns null after its hooks, which is what React requires), so an unconditional poll would
  // put a repeating request behind every room on this site for a surface that can only ever appear on
  // a room landing — a cost with no reader, which is the shape R19 refuses. The three conditions here
  // are the law's own, and the law refuses on every one of them anyway, so a disabled poll can never
  // turn a `no` into an `ask`.
  const couldOpen = signedIn && hasRoom && phonePromptPage(page);
  const { active: phoneGens } = useActiveGenerations(couldOpen);

  // **AND *MID-CONVERSATION* RIDES THE HOUSE'S ONE OVERLAY ANNOUNCEMENT** rather than a second notion
  // of whether something is open over the room (R28/R29): every drawer, sheet and reader on this site
  // already announces itself there to close its siblings. **It never goes back to false on a landing,
  // which is the conservative direction and is deliberate** — once she has opened something the quiet
  // moment has passed, and the popup waits for the next clean landing rather than appearing the
  // instant she closes a thread.
  const [overlayOpened, setOverlayOpened] = useState(false);
  useEffect(() => { setOverlayOpened(false); }, [page]);
  useEffect(() => {
    const seen = () => setOverlayOpened(true);
    window.addEventListener(CLOSE_OVERLAYS_EVENT, seen);
    return () => window.removeEventListener(CLOSE_OVERLAYS_EVENT, seen);
  }, []);

  // The two facts are read ONCE per session. Re-reading on every render would put a request behind
  // every keystroke in the field below.
  useEffect(() => {
    if (!couldOpen || asked.current) return;
    asked.current = true;
    let live = true;
    fetch("/api/member/phone-prompt", { credentials: "include" })
      .then((r) => r.json())
      .then((d) => { if (live) setFacts({ known: d?.known === true, hasPhone: d?.hasPhone !== false, answeredAt: d?.answeredAt ?? null }); })
      // A read that threw is NOT a member with no phone. Left as `known: false`, the law holds and
      // she is asked nothing — the fail-closed direction her *no nagging ever* clause requires.
      .catch(() => {});
    // INSTALL-1's sequencing ruling: the SAME route `InstallPrompt` reads, so there is one store
    // reader and one answer to *has she answered the install* (R28/R29). **`known: false` until it
    // lands means this side HOLDS rather than opening** — a phone sheet that appeared for the beat
    // before the install state arrived is the stacked pair with a delay on it (R18: *we have not
    // looked yet* is not *the install is not offering*).
    fetch("/api/install/state", { credentials: "include" })
      .then((r) => r.json())
      .then((d) => {
        if (!live) return;
        setInstallFacts({
          known: true,
          answer: d?.answer ?? null,
          answeredAt: d?.answeredAt ?? null,
          asks: Number(d?.asks) || 0,
        });
      })
      // A read that threw leaves `known: false`, so nothing opens. Over-holding costs her one ask
      // she meets next landing; opening on an unknown install state is the defect (R19/R8-4).
      .catch(() => {});
    return () => { live = false; };
  }, [couldOpen]);

  const state = phonePromptState({
    known: facts.known, signedIn, hasRoom,
    hasPhone: facts.hasPhone, answeredAt: facts.answeredAt,
    page, generating: phoneGens.length > 0, conversationOpen: overlayOpened,
  });

  // >>> INSTALL-1's SEQUENCING RULING (her word, 2026-08-17): **INSTALL FIRST, PHONE ON A LATER
  // CLEAN LANDING — THEY MAY NEVER STACK.** <<<
  //
  // This component's own header named the risk in advance (*"two different notions of MAY WE
  // INTERRUPT HER on one screen is how two popups end up stacked"*) — and the two notions AGREE,
  // which is exactly why they stacked: both open for a signed-in member with a room on a clean
  // landing, App.tsx mounts both, and neither asked about the other.
  //
  // **THE ANSWER IS ONE SHARED SEQUENCER, NEVER A FLAG THIS SIDE SETS** (R28/R29): `landingOffer`
  // is the one declaration, `shouldOfferInstall` is the one install law, and this side re-decides
  // NOTHING — it asks the same two laws with the same facts and reads which surface may open.
  //
  // **AND IT IS A LATER LANDING RATHER THAN A QUEUE (R18):** nothing here waits for the install
  // sheet to close and then opens. A member who has just been asked one thing is the worst audience
  // for a second ask, so she meets this on a landing where the install offer is not live at all.
  const installAvailable = installFacts.known && shouldOfferInstall({
    standalone: isStandalone(window),
    signedIn, hasRoom,
    priorAnswer: installFacts.answer,
    answeredAt: installFacts.answeredAt,
    asks: installFacts.asks,
    nowMs: Date.now(),
    platform: platformOf(window),
    mobile: Number(window?.innerWidth || 0) <= 900 || platformOf(window) !== PLATFORM.OTHER,
  }).offer;

  if (closed || !phonePromptOffered(state)) return null;
  // The sequencer is asked LAST, so the phone's own laws still decide whether it has anything to
  // ask at all — this only ever decides WHICH of two live offers takes this landing.
  if (!mayOpen({ installAvailable, phoneAvailable: true }, LANDING_OFFERS.PHONE)) return null;

  const answer = async (kind: "save" | "decline") => {
    if (sending.current) return;
    sending.current = true;
    setNote("");
    try {
      const res = await fetch("/api/member/phone-prompt", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(kind === "save" ? { answer: "save", phone, smsConsent: consent } : { answer: "decline" }),
      });
      // R39/R47: these doors answer HTTP 200 with `{ ok: false }`, so a client reading the status
      // code calls a refusal a success. Read the payload.
      const data = await res.json().catch(() => null);
      if (data?.ok === true) { setClosed(true); return; }
      // A number this house cannot dial leaves the popup OPEN with her sentence beside it — the one
      // state in this round where staying open is right, because closing loses her the number she
      // was in the middle of giving.
      setNote(String(data?.message || "That did not save just now. Try again in a moment."));
    } catch {
      setNote("That did not save just now. Try again in a moment.");
    } finally {
      sending.current = false;
    }
  };

  return (
    <div className="phone-pop__backdrop" role="dialog" aria-modal="true" aria-labelledby="phone-pop-title">
      <div className="phone-pop">
        <span className="phone-pop__mark" aria-hidden="true"><MessageSquare size={22} /></span>
        {/* HER CLAUSE: THE REASON BEFORE THE ASK. A member shown a field first is being asked for
            something; a member told what it buys her first is being offered something. */}
        <h2 id="phone-pop-title" className="phone-pop__title">{PHONE_PROMPT_COPY.title}</h2>
        <p className="phone-pop__body">{PHONE_PROMPT_COPY.lede}</p>

        <label className="phone-pop__field">
          <span className="visually-hidden">Phone number</span>
          <input
            type="tel"
            name="phonePromptNumber"
            autoComplete="tel"
            inputMode="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="(555) 555-5555"
          />
        </label>

        {/* C8b's ONE consent sentence, imported rather than re-typed: three doors promising three
            different things is what that declaration exists to make impossible (R28/R29). It is
            UNCHECKED, and it never blocks the save — a number given for account recovery is still
            a number this house may use to reach her about her own account. */}
        <label className="consent-check phone-pop__consent">
          <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
          <span className="consent-check__text">{SMS_CONSENT_TEXT}</span>
        </label>

        {note ? <p className="phone-pop__note" role="alert">{note}</p> : null}

        <button type="button" className="phone-pop__go" onClick={() => answer("save")}>
          {PHONE_PROMPT_COPY.save}
        </button>
        {/* HER *plain "not now"* — and it is a real answer that reaches the database, not a dismiss.
            A close that recorded nothing would put this back in front of her tomorrow. */}
        <button type="button" className="phone-pop__later" onClick={() => answer("decline")}>
          {PHONE_PROMPT_COPY.decline}
        </button>
      </div>
    </div>
  );
}
